import { formatMoney } from '@tix/common-utils'
import type { Schema } from '@tix/openapi-sdk'

import type { DonationCustomAmount, DonationPreset } from '@/components/donations/DonationWidget.vue'

type TicketType = Schema<'ticket_type'>

const isVariablePrice = (ticketType: TicketType): boolean => ticketType.price_strategy === 'variable'

/**
 * Buttons for preset donation amounts, ordered by `_rank`.
 */
export function buildDonationPresets(ticketTypes: TicketType[]): DonationPreset[] {
  return ticketTypes
    .filter((ticketType) => !isVariablePrice(ticketType))
    .sort((a, b) => a._rank - b._rank)
    .map((ticketType) => ({
      id: ticketType.id,
      label: formatMoney(ticketType.currency_amount, ticketType.currency_code),
    }))
}

/**
 * Determine if a donation event should support a custom amount field
 * by looking for a variable-priced ticket type.
 */
export function buildDonationCustomAmount(ticketTypes: TicketType[]): DonationCustomAmount | null {
  const variable = ticketTypes.find(isVariablePrice)
  if (!variable) return null

  return {
    id: variable.id,
    currency: variable.currency_code,
    min: variable.currency_amount_min != null ? Number(variable.currency_amount_min) : undefined,
    max: variable.currency_amount_max != null ? Number(variable.currency_amount_max) : undefined,
  }
}

/**
 * Parse a raw custom amount into a reservable selection, or `null` if it can't
 * be reserved (empty/invalid amount, out of range, too many decimals, or no
 * variable-price option). Returning the built selection lets callers avoid
 * re-checking `amount`/`customAmount` for null.
 */
export function getDonationReserveSelection(
  amount: number | null,
  customAmount: DonationCustomAmount | null | undefined,
): { ticketTypeId: string; faceValue: number } | null {
  if (amount === null || !Number.isFinite(amount) || !customAmount) {
    return null
  }

  if (customAmount.min != null && amount < customAmount.min) {
    return null
  }

  if (customAmount.max != null && amount > customAmount.max) {
    return null
  }

  if (Number(amount.toFixed(2)) !== amount) {
    return null
  }

  return { ticketTypeId: customAmount.id, faceValue: amount }
}
